import React, { useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import AttributionCell from '~/components/GHADiscounting/AttributionCell';
import { CUG_TIER_LABELS } from '~/components/GHAPropertyManagement/constants';

import {
  useDeleteGHACugTierChainRule,
  useGHACugTierChainRules,
  useUpsertGHACugTierChainRule,
} from '~/hooks/apis/connection-google/useGHACugTierChainRules';

import type { GHACugTierChainRule } from '~/services/ConnectionGoogleService';

import CugTierChainRuleDialog from './CugTierChainRuleDialog';

// GHA-438: the chains the daily CUG tier job raises to Tier 1 or Tier 2. Each row maps one bedbank
// chain name to a tier; the job reads this list on every run.
export default function GHACugTierChainsContainer() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<GHACugTierChainRule | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const showConfirmDialog = useConfirmDialog();

  const { data, isLoading, error } = useGHACugTierChainRules();
  const upsertMutation = useUpsertGHACugTierChainRule();
  const deleteMutation = useDeleteGHACugTierChainRule();

  const rules = useMemo(
    () =>
      [...(data ?? [])].sort((a, b) => a.cugTier.localeCompare(b.cugTier) || a.chainName.localeCompare(b.chainName)),
    [data],
  );

  const handleDelete = async (rule: GHACugTierChainRule) => {
    const confirmed = await showConfirmDialog({
      title: `Delete ${rule.chainName}?`,
      description:
        'The daily job stops raising properties of this chain. Properties it already raised keep their tier; change those by hand if needed.',
    });
    if (confirmed) {
      deleteMutation.mutate(rule);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setRuleToEdit(null);
    setSubmitError(null);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">Failed to load CUG tier chains</Alert>;
    }
    if (rules.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No chains.
        </Typography>
      );
    }
    return (
      <Table size="small" sx={{ tableLayout: 'fixed', '& td, & th': { py: 1 } }}>
        <TableHead>
          <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
            <TableCell sx={{ width: '25%' }}>Chain</TableCell>
            <TableCell sx={{ width: '10%' }}>Tier</TableCell>
            <TableCell sx={{ width: '25%' }}>Reason</TableCell>
            <TableCell sx={{ width: '24%' }}>Created / updated</TableCell>
            <TableCell align="right" sx={{ width: '16%' }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rules.map((rule) => (
            <TableRow key={rule.id}>
              <TableCell>{rule.chainName}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{CUG_TIER_LABELS[rule.cugTier]}</TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {rule.reason ?? '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <AttributionCell
                  createdBy={rule.createdBy}
                  updatedBy={rule.updatedBy}
                  createdAt={rule.createdAt}
                  updatedAt={rule.updatedAt}
                />
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Button
                  size="small"
                  onClick={() => {
                    setRuleToEdit(rule);
                    setDialogOpen(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  disabled={deleteMutation.isPending}
                  onClick={() => handleDelete(rule)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Container>
      <Helmet>
        <title>GHA CUG Tier Chains</title>
      </Helmet>
      <PageSubheader title="GHA CUG Tier Chains" />

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New chain
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Every day at 05:00 Sydney, properties whose bedbank chain is listed here are raised to that tier. The job only
        raises tiers: deleting a chain or lowering its tier never moves properties back down. Flash and LPC history
        raise tiers too, separately from this list.
      </Alert>

      {renderContent()}

      <CugTierChainRuleDialog
        open={dialogOpen}
        initialRule={ruleToEdit}
        isSaving={upsertMutation.isPending}
        submitError={submitError}
        onClose={closeDialog}
        onSubmit={async (input) => {
          setSubmitError(null);
          try {
            await upsertMutation.mutateAsync(input);
            closeDialog();
          } catch (submitException) {
            setSubmitError(submitException instanceof Error ? submitException.message : 'Failed to save the chain');
          }
        }}
      />
    </Container>
  );
}
